import { Col, Row } from 'antd';
import config from 'config';
import { OwnerType, PermissionsEnum, UserOutput, UserRole } from 'gql/types';
import EditUserEmailForm from 'modules/admin/components/EditUserForm/EmailSection';
import { UserType } from 'modules/admin/components/EditUserForm/EmailSection/enum';
import { FormItem } from 'modules/common/components';
import ControlledDatePicker from 'modules/common/components/ControlledDatePicker';
import { PersonalInfoFields } from 'modules/owner/components/EditOwnerProfileForm/PersonalInfoSection/enum';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { personalInfoFields } from './fields';
import { personalInfoSchema } from './schema';

import ReadOnlyDatePicker from '../../../../common/components/ReadOnlyDatePicker';

type Props = {
  fieldNamePrefix: string;
  enabled: boolean;
  owner: UserOutput;
};

const PersonalInfoFormSection: FC<Props> = ({
  fieldNamePrefix,
  enabled,
  owner,
}) => {
  const { t } = useTranslation(
    'owner.EditOwnerProfileForm.PersonalInfoSection'
  );
  const currentUser = useCurrentUser();
  const currentUserRole = currentUser.data?.role.value;
  const isOwner = currentUserRole === UserRole.Owner;
  const isIndividualOwner = owner.type === OwnerType.Individual;
  const allowToChangeEmail =
    currentUser.isCan(PermissionsEnum.UpdateOwnersEmail) && !owner.deletedAt;
  const allowToViewCreationDate =
    !!currentUserRole &&
    [UserRole.Admin, UserRole.MedicalDirector, UserRole.InventoryTeam].includes(
      currentUserRole
    );

  const isFieldDisabled = (field: PersonalInfoFields) => {
    if (!enabled) {
      return true;
    }

    const isLocationPrefs = field === PersonalInfoFields.locationPreferences;
    const shouldDisableLocationPrefs =
      isIndividualOwner && isOwner && isLocationPrefs;

    if (shouldDisableLocationPrefs) {
      return true;
    }

    return false;
  };

  const { control, formState, getFieldState } = useFormContext();

  const birthdayFieldName = `personalInfo.${PersonalInfoFields.birthday}`;
  const { error: birthdayError } = getFieldState(birthdayFieldName, formState);

  return (
    <Col span={24}>
      <h2>{t('personalInfo')}</h2>
      {personalInfoFields.map(({ name, label, Component }) => {
        const fieldName = `${fieldNamePrefix}.${name}`;
        const { error } = getFieldState(fieldName, formState);
        const isRequired =
          (personalInfoSchema.fields[name] as { spec?: { presence?: string } })
            ?.spec?.presence === 'required';

        return (
          <Row key={fieldName}>
            <Col span={24}>
              <FormItem
                required={isRequired}
                label={label}
                extra={error?.message}
                validateStatus={error ? 'error' : undefined}
              >
                <Component
                  disabled={isFieldDisabled(name)}
                  control={control}
                  name={fieldName}
                />
              </FormItem>
            </Col>
          </Row>
        );
      })}
      <Row key={PersonalInfoFields.birthday}>
        <Col span={24}>
          <FormItem
            label={t('birthday')}
            extra={birthdayError?.message}
            validateStatus={birthdayError ? 'error' : undefined}
          >
            <ControlledDatePicker
              disabled={!enabled}
              placeholder={t('selectBirthdayDate')}
              controlled={{
                control,
                name: birthdayFieldName,
              }}
            />
          </FormItem>
        </Col>
      </Row>
      <EditUserEmailForm
        user={owner}
        canEdit={allowToChangeEmail}
        userType={UserType.Owner}
      />

      {allowToViewCreationDate && (
        <FormItem label={t('dateOfCreation')}>
          <ReadOnlyDatePicker
            value={new Date(owner.createdAt)}
            format={config.DATE_FORMATS.DATE}
          />
        </FormItem>
      )}
    </Col>
  );
};

export default PersonalInfoFormSection;
